import React, { useState } from 'react';
import axios from 'axios';

import { reportAxiosError } from '../../../../../Utils';
import CardDialog from '../../../../CardDialog';
import { Caption, FsButton, FsIconButton, Heading, LoadingSwitch } from '../../../../core';
import { PencilIcon } from '../../../../icons';
import useDataFetcher from '../../../../useDataFetcher';
import { useSysconfig } from '../../../SystemConfigurationScreen';

import OneIncConfigurationFormDialog from './ConfigurationFormDialog';
import Detail from './Detail';
import OneIncInboundPaymentsCard from './InboundPaymentsCard';

import styles from './oneIncInternalAdmin.module.scss';

const InternalAdminOneInc = () => {
  const [configurationFormOpen, setConfigurationFormOpen] = useState(false);
  const { organization } = useSysconfig();
  const configUrl = `/api/v1/one_inc/config/organizations/${organization.id}`;
  const { isLoading, isError, data, reloadData } = useDataFetcher(configUrl);

  const isEnabled = !!data?.id;

  const handleSubmitOneIncConfiguration = async (values) => {
    try {
      await axios.put(configUrl, values);
      await reloadData();
      setConfigurationFormOpen(false);
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };
  const revealWebhookPassword = async () => {
    try {
      const { data: credentials } = await axios.get(`${configUrl}/webhook_credentials`);
      return credentials.webhook_password;
    } catch (error) {
      await reportAxiosError(error);
      return undefined;
    }
  };

  // The two cards fetch separately and are mounted separately: `LoadingSwitch` renders *only* the
  // error indicator when `isError`, so wrapping both in this one would let a ClaimsPay fetch failure
  // hide the PremiumPay card -- and the two tenants share no credential, so configuring one must
  // never require the other to be reachable.
  return (
    <CardDialog noCardTitle containerClassName={styles.oneIncInternalAdmin}>
      <LoadingSwitch isLoading={isLoading} isError={isError}>
        {/* Both states are a card, and the card names the product. Two One Inc products share this
            tab -- ClaimsPay pays money out, PremiumPay takes it in -- with separate tenants and no
            shared credential, so a bare enable button above a titled card read as a prerequisite
            the code does not have. */}
        <div className={styles.containerWithBorder}>
          <div className={styles.sectionTitle}>
            <Heading variant={Heading.TYPES.H4}>ClaimsPay (outbound payments)</Heading>
            {isEnabled ? (
              <FsIconButton
                icon={PencilIcon}
                onClick={() => {
                  setConfigurationFormOpen(true);
                }}
              />
            ) : null}
          </div>
          {isEnabled ? (
            <>
              <div className={styles.sectionDetails}>
                <Detail label="Username" text={data?.username} />
                <Detail label="Password" text={data?.password} />
                <Detail label="URL" text={data?.url} />
              </div>
              {/* Nested rather than a third peer card: this webhook is ClaimsPay's own feedback
                  endpoint, so as a sibling it read as a One Inc product in its own right. */}
              <div className={styles.subSectionTitle}>
                <Heading variant={Heading.TYPES.H4}>Webhook</Heading>
              </div>
              <div className={styles.sectionDetails}>
                <Detail label="Username" text={data?.webhook_username} />
                <Detail label="Password" text={data?.webhook_password} isSecret onReveal={revealWebhookPassword} />
                <Detail label="Webhook - URL" text={data?.webhook_url} />
              </div>
            </>
          ) : (
            <>
              <div className={styles.emptyStateDescription}>
                <Caption variant={Caption.VARIANTS.LABEL}>
                  Send claim payments to payees through One Inc ClaimsPay.
                </Caption>
              </div>
              <FsButton
                color="primary"
                variant="contained"
                className={styles.button}
                onClick={() => {
                  setConfigurationFormOpen(true);
                }}
              >
                Enable ClaimsPay
              </FsButton>
            </>
          )}
        </div>
      </LoadingSwitch>
      <OneIncInboundPaymentsCard />
      {configurationFormOpen ? (
        <OneIncConfigurationFormDialog
          onSubmit={handleSubmitOneIncConfiguration}
          configuration={data}
          onClose={() => {
            setConfigurationFormOpen(false);
          }}
        />
      ) : null}
    </CardDialog>
  );
};

export default InternalAdminOneInc;
